import { ViewChild, ElementRef } from '@angular/core'
import { Location } from '@angular/common'
import { URLSearchParams } from '@angular/http'
import { Observable } from 'rxjs/Observable'
import { Subscription } from 'rxjs/Subscription'
import { DragulaService } from 'ng2-dragula/src'
import * as _ from 'lodash'

import { APP_INJECTOR } from 'app.module'
import { BasePageComponent } from 'core/abstract-components'
import { TextSearchComponent, LabelFilter } from 'common/search'
import { Folder } from 'models'
import { BaseFolderTreeService } from './base-folder-tree.service'
import { BaseFolderComponent } from './base-folder.component'
import { FolderFormComponent } from './folder-form.component'
import { Scroll } from 'util/scroll'

export abstract class BaseFolderTreePageComponent extends BasePageComponent {
  @ViewChild(FolderFormComponent) protected folderFormCmp: FolderFormComponent
  @ViewChild(TextSearchComponent) protected textSearchCmp: TextSearchComponent

  protected location: Location

  protected perPage: number = 20
  protected paginationTotal: number = 0
  protected currentPage: number
  protected sort: string
  protected sortDirection: string
  protected searchParams: Object
  protected labelFilters: LabelFilter[] = []
  protected includeDisabled: boolean

  protected rootFolder: Folder
  protected pageFolders: Folder[] = []

  protected dragSubscription: Subscription
  protected dragendSubscription: Subscription
  protected dropSubscription: Subscription

  protected loading: boolean = false
  protected dragging: boolean = false
  protected scrollTop: number
  protected isTextSearchOpen: boolean = false

  constructor(
    protected elRef: ElementRef,
    protected treeService: BaseFolderTreeService,
    protected dragulaService: DragulaService
  ) {
    super()

    this.isFolderActive = this.isFolderActive.bind(this)

    this.location = APP_INJECTOR.get(Location)
  }

  ngOnInit() {
    super.ngOnInit()

    this.dragulaService.setOptions('folderTree', {
      copy: true,
      mirrorContainer: this.elRef.nativeElement,
      accepts: (el, container, source, sibling) => {
        return this.folderTreeDroppable(el, container, source, sibling)
      }
    })
    this.dragSubscription = this.dragulaService.drag.subscribe(event => {
      this.onDrag(event)
    })
    this.dragendSubscription = this.dragulaService.dragend.subscribe(event => {
      this.onDragend(event)
    })
    this.dropSubscription = this.dragulaService.drop.subscribe(event => {
      this.onDrop(event)
    })

    this.isTextSearchOpen = !!this.getSearchParam('query')
  }

  ngOnDestroy() {
    super.ngOnDestroy()

    this.dragSubscription.unsubscribe()
    this.dragendSubscription.unsubscribe()
    this.dropSubscription.unsubscribe()
    this.dragulaService.destroy('folderTree')
  }

  getSearchParam(key: string): any {
    return this.searchParams[key]
  }

  setSearchParam(key: string, value: any) {
    let params = {}
    params[key] = value
    this.setSearchParams(params)
  }

  setIncludeDisabledItems(yes: boolean) {
    this.includeDisabled = yes
    this.onSearchParamsChange()
  }

  setSearchParams(params: Object) {
    let result = this.searchParams
    let changed = false
    for(let key in params) {
      let value = params[key]
      if(value == result[key]) { continue }
      if(value == undefined || value == null) {
        delete result[key]
      } else {
        result[key] = value
      }
      changed = true
    }
    if(changed) {
      this.searchParams = result
      this.onSearchParamsChange()
    }
  }

  setPage(page: number) {
    if(page != this.currentPage) {
      this.currentPage = page
      this.onPageChange()
    }
  }

  setSort(sort: string, sortDirection: string) {
    if(sort != this.sort || sortDirection != this.sortDirection) {
      this.currentPage = 1
      this.sort = sort
      this.sortDirection = sortDirection
      this.onSortChange()
    }
  }

  protected onSearchParamsChange() {
    this.scrollTop = 0
    this.loadData()
  }

  protected onIncludeDisableChange() {
    this.loadData()
  }

  protected onPageChange() {
    this.treeService.closeAllFolders()
    this.updatePagination()
  }

  protected onSortChange() {
    this.scrollTop = 0
    this.loadData()
  }

  protected buildFetchParams(): Object {
    let result = _.cloneDeep(this.searchParams)
    let labelFilters = this.labelFilters.reduce((hash, filter) => {
      hash[filter.requestKey] = filter.value
      return hash
    }, {})
    Object.assign(result, labelFilters)
    if(this.sort)          { result['sort'] = this.sort }
    if(this.sortDirection) { result['sortDirection'] = this.sortDirection }
    if(this.includeDisabled) { result['includeDisabled'] = this.includeDisabled }
    return result
  }

  protected searching(): boolean {
    return !_.isEmpty(this.searchParams) || !_.isEmpty(this.labelFilters)
  }

  protected loadData() {
    this.loading = true
    this.treeService.setFetchParams(this.buildFetchParams())
    this.treeService.loadData().subscribe(
      data => {
        this.rootFolder = this.treeService.getRootFolder()
        this.updatePagination()
        setTimeout(() => {
          Scroll.top(this.scrollTop)
          this.loading = false
        }, 10)
      },
      error => {
        this.alertService.error(error)
        this.loading = false
      }
    )
  }

  protected updatePagination() {
    let folders = this.rootFolder.subFolders.filter((folder: Folder) => { return this.isFolderActive(folder) })
    let total = folders.length
    let totalPages = Math.ceil(total / this.perPage)
    let page = this.currentPage > totalPages ? totalPages : this.currentPage
    if(page < 1) { page = 1 }
    let offsetStart = (page - 1) * this.perPage
    let offsetEnd = offsetStart + this.perPage

    this.paginationTotal = total
    this.currentPage = page
    this.pageFolders = folders.slice(offsetStart, offsetEnd)
  }

  protected isFolderActive(folder: Folder): boolean {
    return !this.searching() || folder.itemsCount > 0
  }

  protected showNewFolderForm() {
    this.folderFormCmp.showNew({ ancestry: this.rootFolder.childrenAncestry() })
  }

  protected afterCreateFolder(event: Object) {
    let folder = event['folder'] as Folder
    this.treeService.loadSubFoldersOf(folder.parentId).subscribe(
      data => {
        if(folder.depth() == 1) { this.updatePagination() }
      },
      error => { this.alertService.error(error) }
    )
  }

  protected afterUpdateFolder(event: Object) {
    let folder = event['folder'] as Folder
    this.treeService.loadSubFoldersOf(folder.parentId).subscribe(
      data => {
        if(folder.depth() == 1) { this.updatePagination() }
      },
      error => { this.alertService.error(error) }
    )
  }

  protected afterDestroyFolder(event: Object) {
    let folder = event['folderWas'] as Folder
    this.treeService.closeFolderSubtree(folder.id)
    this.treeService.loadSubFoldersOf(folder.parentId).subscribe(
      data => {
        if(folder.depth() == 1) { this.updatePagination() }
      },
      error => { this.alertService.error(error) }
    )
  }

  protected openTextSearch() {
    this.isTextSearchOpen = true
    setTimeout(() => {
      this.textSearchCmp.setFocus()
    })
  }

  protected closeTextSearch() {
    this.isTextSearchOpen = false
    this.setSearchParam('query', null)
  }

  protected onTextSearchKeyup(event) {
    if(event.keyCode == 27) {
      this.closeTextSearch()
    }
  }

  protected folderTreeDroppable(el, container, source, sibling): boolean {
    if(!el.dataset['dragFolderId'] || !container.dataset['dropFolderId']) { return false }

    return this.treeService.folderMoveable(
      +el.dataset['dragFolderId'],
      +container.dataset['dropFolderId']
    )
  }

  protected onDrag(event: any[]) {
    let [bagName, el, source] = event
    this.dragging = true
  }

  protected onDragend(event: any[]) {
    let [bagName, el] = event
    this.dragging = false
  }

  protected onDrop(event: any[]) {
    let [bagName, el, container, source, sibling] = event
    if(
      bagName != 'folderTree' ||
      !container || !container.dataset['dropFolderId'] ||
      !el.dataset['dragFolderId']
    ) { return }

    el.remove()

    let folder = this.treeService.findFolder(+el.dataset['dragFolderId'])
    let newParent = this.treeService.findFolder(+container.dataset['dropFolderId'])
    let prevParent = this.treeService.findFolder(folder.parentId)

    this.loading = true
    this.treeService.moveFolder(folder.id, newParent.id).subscribe(
      folder => {
        Observable.forkJoin([
          this.treeService.loadSubFoldersOf(newParent.id), this.treeService.loadSubFoldersOf(prevParent.id)
        ]).subscribe(
          data => {
            this.updatePagination()
            this.loading = false
          },
          error => {
            this.alertService.error(error)
            this.loading = false
          }
        )
      },
      error => {
        this.alertService.error(error)
        this.loading = false
      }
    )
  }

  protected onRemoveLabelFilter(filter: LabelFilter) {
    let index = this.labelFilters.indexOf(filter)
    if(index > -1) {
      this.labelFilters.splice(index, 1)
      let queryParams = new URLSearchParams(window.location.search.slice(1))
      queryParams.delete(filter.key)
      let query = queryParams.toString()
      let path = query ? `${window.location.pathname}?${query}` : window.location.pathname
      this.location.replaceState(path)
      this.afterRemoveLabelFilter(filter)
    }
  }

  protected afterRemoveLabelFilter(filter: LabelFilter) {
    this.loadData()
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./app/common/folder-tree/base-folder-tree-page.component.ts